import { useState, useEffect } from 'react'
import {
  Spinner, Alert, Form, Button, Card, Table, Modal, Accordion
} from 'react-bootstrap'
import client from '../api/client'

function Projects() {
  const [projects, setProjects] = useState([])
  const [expandedProject, setExpandedProject] = useState(null)
  const [conversations, setConversations] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingProject, setEditingProject] = useState(null)
  const [formData, setFormData] = useState({ name: '', context: '' })

  useEffect(() => {
    fetchProjects()
  }, [])

  const fetchProjects = async () => {
    setLoading(true)
    try {
      const response = await client.get('/api/v1/chat/projects')
      setProjects(response.data.projects || [])
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const fetchConversations = async (projectId) => {
    if (conversations[projectId]) {
      return
    }

    try {
      const response = await client.get(`/api/v1/chat/projects/${projectId}/conversations`)
      setConversations(prev => ({
        ...prev,
        [projectId]: response.data.conversations || []
      }))
    } catch (err) {
      console.error('Erreur lors du chargement des conversations:', err)
    }
  }

  const handleToggleProject = (projectId) => {
    if (expandedProject === projectId) {
      setExpandedProject(null)
    } else {
      setExpandedProject(projectId)
      fetchConversations(projectId)
    }
  }

  const handleAdd = () => {
    setEditingProject(null)
    setFormData({ name: '', context: '' })
    setShowModal(true)
  }

  const handleEdit = (project) => {
    setEditingProject(project)
    setFormData({
      name: project.name,
      context: project.context || ''
    })
    setShowModal(true)
  }

  const handleDelete = async (projectId) => {
    if (!window.confirm('Supprimer ce projet et toutes ses conversations ?')) return

    try {
      await client.delete(`/api/v1/chat/projects/${projectId}`)
      setProjects(prev => prev.filter(p => p.id !== projectId))
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression')
      console.error(err)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    try {
      if (editingProject) {
        const response = await client.patch(`/api/v1/chat/projects/${editingProject.id}`, formData)
        setProjects(prev => prev.map(p => p.id === editingProject.id ? response.data : p))
      } else {
        const response = await client.post('/api/v1/chat/projects', formData)
        setProjects(prev => [response.data, ...prev])
      }
      setShowModal(false)
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde')
      console.error(err)
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp)
    return date.toLocaleString('fr-FR')
  }

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Projets / Workspaces</h2>
        <Button variant="primary" onClick={handleAdd}>
          <i className="bi bi-plus-circle"></i> Nouveau Projet
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {projects.length > 0 ? (
        <Accordion activeKey={expandedProject}>
          {projects.map(project => (
            <Accordion.Item eventKey={project.id} key={project.id}>
              <Accordion.Header onClick={() => handleToggleProject(project.id)}>
                <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                  <div>
                    <strong>{project.name}</strong>
                    {project.context && (
                      <small className="text-muted d-block">
                        {project.context.substring(0, 80)}
                        {project.context.length > 80 && '...'}
                      </small>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEdit(project)
                      }}
                    >
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete(project.id)
                      }}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <div className="mb-3">
                  <strong>ID:</strong> {project.id}
                  <br />
                  <strong>Créé:</strong> {formatDate(project.created_at)}
                  <br />
                  {project.context && (
                    <>
                      <strong>Contexte:</strong>
                      <div className="mt-2 p-2 bg-light rounded">
                        {project.context}
                      </div>
                    </>
                  )}
                </div>

                <h6>Conversations ({conversations[project.id]?.length || 0})</h6>
                {conversations[project.id] && conversations[project.id].length > 0 ? (
                  <Table striped size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>Titre</th>
                        <th>Messages</th>
                        <th>Créée</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversations[project.id].map(conv => (
                        <tr key={conv.id}>
                          <td>{conv.title || 'Sans titre'}</td>
                          <td>{conv.message_count || 0}</td>
                          <td><small>{formatDate(conv.created_at)}</small></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <Alert variant="info" className="mb-0">
                    Aucune conversation dans ce projet.
                  </Alert>
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      ) : (
        <Alert variant="info">
          Aucun projet. Créez-en un avec le bouton ci-dessus.
        </Alert>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingProject ? 'Modifier' : 'Créer'} un Projet</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nom du projet</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Projet Marketing"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Contexte (optionnel)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={formData.context}
                onChange={(e) => setFormData({ ...formData, context: e.target.value })}
                placeholder="Contexte ou instructions pour ce projet..."
              />
              <Form.Text className="text-muted">
                Le contexte sera inclus dans toutes les conversations de ce projet.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit">
              {editingProject ? 'Modifier' : 'Créer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default Projects
